import React, { useEffect, useRef } from 'react';
import * as echarts from 'echarts';
import { TelemetryPanelConfig, TelemetryUnitSystem, TelemetryViewportState } from '../types/telemetry-dashboard.types';
import { isTelemetryChartViz, getTelemetrySubColor, getTelemetryVizLabel } from '../utils/telemetry-color.utils';
import { getSubsystemLabel, getChannelDef } from '../utils/telemetry-helpers.utils';
import { optionFor, setPanelLegendSelected } from '../utils/telemetry-chart-options.utils';
import {
  TelemetryCard,
  TelemetryCardHeader,
  TelemetryCardTitle,
  TelemetryCardAlertBadge,
  TelemetryCardActions,
  TelemetryCardVizSelect,
  TelemetryCardBody,
} from './TelemetryCardComponents';
import { TelemetryCardChart } from './TelemetryPanel';
import '../styles/telemetry-dashboard-panel.css';

export interface TelemetryExpandedModalProps {
  panel: TelemetryPanelConfig | any | null;
  timeframe?: number;
  precision?: number;
  units?: TelemetryUnitSystem | any;
  viewport?: TelemetryViewportState | { spanMs: number; offsetMs: number };
  onClose: () => void;
  renderHeaderActions?: (panel: any) => React.ReactNode;
  renderVizSelect?: (panel: any) => React.ReactNode;
  onVizClick?: (e: React.MouseEvent) => void;
  activeMenuTriggerId?: string | null;
  patchWidget?: (id: string, patch: Partial<any>) => void;
}

export const TelemetryExpandedModal: React.FC<TelemetryExpandedModalProps> = ({
  panel,
  timeframe = 5,
  precision = 2,
  units = {},
  viewport = { spanMs: 300000, offsetMs: 0 },
  onClose,
  renderHeaderActions,
  renderVizSelect,
  onVizClick,
  activeMenuTriggerId,
  patchWidget,
}) => {
  const chartWrapRef = useRef<HTMLDivElement>(null);
  const ecRef = useRef<echarts.ECharts | null>(null);
  const resizeHandlerRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!panel) return;
    const isChart = isTelemetryChartViz(panel.graphMode) || panel.graphMode === 'cchart' || panel.graphMode === 'line' || panel.graphMode === 'area';
    if (!isChart) return;

    if (ecRef.current) {
      ecRef.current.dispose();
      ecRef.current = null;
    }
    if (resizeHandlerRef.current) {
      window.removeEventListener('resize', resizeHandlerRef.current);
      resizeHandlerRef.current = null;
    }

    const timer = setTimeout(() => {
      const node = chartWrapRef.current;
      if (!node || typeof echarts === 'undefined') return;
      const ec = echarts.init(node, undefined, { renderer: 'canvas' });
      ec.on('legendselectchanged', (params: any) => {
        if (params?.selected && panel?.id) {
          setPanelLegendSelected(panel.id, params.selected);
        }
      });
      const opt = optionFor(panel as any, timeframe, precision, units as any, viewport as any);
      ec.setOption(opt);
      ec.resize();
      ecRef.current = ec;

      const handleResize = () => ec.resize();
      resizeHandlerRef.current = handleResize;
      window.addEventListener('resize', handleResize);
    }, 50);

    return () => {
      clearTimeout(timer);
      if (resizeHandlerRef.current) {
        window.removeEventListener('resize', resizeHandlerRef.current);
        resizeHandlerRef.current = null;
      }
      if (ecRef.current) {
        ecRef.current.dispose();
        ecRef.current = null;
      }
    };
  }, [panel, timeframe, precision, units, viewport]);

  if (!panel) return null;

  const first = panel.parameters?.[0] || { sub: '', tel: '' };
  const def = getChannelDef(first.sub, first.tel);
  const title = panel.name || def?.label || first.tel || 'Expanded Panel';
  const subLabel = getSubsystemLabel(first.sub);
  const vizLabel = getTelemetryVizLabel(panel.graphMode);
  const subColor = getTelemetrySubColor(first.sub);
  const subtitleMeta = `${subLabel} · ${vizLabel}`;

  const handleContainerClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const large = panel.parameters?.length > 65;

  return (
    <div id="telemetry-expanded-modal-overlay" className="telemetry-modal-overlay" onClick={onClose}>
      <div id="telemetry-expanded-modal-container" className={`telemetry-modal-container ${large ? 'telemetry-modal-container-large' : ''}`} onClick={handleContainerClick}>
        <TelemetryCard panel={panel} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <TelemetryCardHeader panel={panel}>
            <TelemetryCardTitle title={title} subColor={subColor} subtitle={subtitleMeta}>
              {title}
            </TelemetryCardTitle>

            <TelemetryCardAlertBadge panel={panel} />

            <TelemetryCardActions>
              {renderVizSelect ? (
                renderVizSelect(panel)
              ) : onVizClick ? (
                <TelemetryCardVizSelect
                  panel={panel}
                  activeMenuTriggerId={activeMenuTriggerId}
                  onVizClick={onVizClick}
                />
              ) : null}

              {renderHeaderActions && renderHeaderActions(panel)}

              <button
                id="telemetry-expanded-modal-close-btn"
                className="telemetry-panel-action-btn"
                style={{ fontSize: 16, cursor: 'pointer', padding: '2px 6px', color: '#8E8E8E' }}
                onClick={onClose}
                title="Close expanded view"
              >
                ✕
              </button>
            </TelemetryCardActions>
          </TelemetryCardHeader>

          <TelemetryCardBody style={{ flex: 1, minHeight: 0 }}>
            <TelemetryCardChart
              panel={panel}
              graphType={panel.graphMode}
              timeframe={timeframe}
              precision={precision}
              units={units}
              viewport={viewport}
              chartWrapRef={(el) => {
                (chartWrapRef as React.MutableRefObject<HTMLDivElement | null>).current = el as HTMLDivElement;
              }}
              patchWidget={patchWidget}
              activeMenuTriggerId={activeMenuTriggerId}
            />
          </TelemetryCardBody>
        </TelemetryCard>
      </div>
    </div>
  );
};

export default TelemetryExpandedModal;
